// Resolución de un fixture importado desde Excel contra los equipos de una zona.
//
// El archivo que se sube trae los partidos de TODAS las zonas de la jornada: el
// mismo archivo se importa parado en cada zona y acá se decide qué filas le
// corresponden. La zona no se lee del Excel, se infiere por los equipos: una fila
// es de esta zona cuando sus dos equipos están en zone.teamIds.

// Normalización para comparar nombres de equipo. Más agresiva que cleanString
// (que sólo reemplaza la primera aparición de cada acento y no toca la Ñ):
// saca acentos, puntuación y espacios repetidos.
export function normalizarNombre(valor) {
  if (!valor) return "";
  return String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // acentos
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, " ") // puntos, guiones, comillas
    .replace(/\s+/g, " ")
    .trim();
}

// "10:30" a partir de lo que venga. El caso normal ya llega normalizado del server
// (que es quien ve el valor crudo de la celda); acá toleramos además "10.30",
// "10 30" y "1030". Devuelve "" si no se puede interpretar.
export function normalizarHora(valor) {
  if (valor === null || valor === undefined || valor === "") return "";
  const texto = String(valor).trim();
  const m = texto.match(/^(\d{1,2})[:.\s]?(\d{2})$/);
  if (!m) return "";
  const horas = Number(m[1]);
  const minutos = Number(m[2]);
  if (horas > 23 || minutos > 59) return "";
  return String(horas).padStart(2, "0") + ":" + String(minutos).padStart(2, "0");
}

// La cancha es un Number 1..4 en el modelo. Fuera de rango => "" (se carga a mano).
export function normalizarCancha(valor) {
  if (valor === null || valor === undefined || valor === "") return "";
  const soloDigitos = String(valor).replace(/\D/g, "");
  if (!soloDigitos) return "";
  const numero = Number(soloDigitos);
  return numero >= 1 && numero <= 4 ? String(numero) : "";
}

// Umbral a partir del cual se ofrece una sugerencia, y a partir del cual además viene
// preseleccionada. Calibrados contra los nombres reales: "OLD GEORGIAN BCO" ->
// "OLD GEORGIAN BLANCO" da 0.97, mientras que los pares que se parecen pero son
// equipos distintos ("CRUZ DEL SUR A"/"B", "TDH B"/"D", "MUVI AZUL"/"BLANCO",
// "LAS ORANGE"/"LAS ORANGE BLUE") quedan todos por debajo de 0.72.
export const UMBRAL_SUGERENCIA = 0.72;
export const UMBRAL_PRESELECCION = 0.85;

// Distancia de edición (Levenshtein), con dos filas en vez de la matriz completa.
function distanciaEdicion(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let anterior = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const actual = [i];
    for (let j = 1; j <= b.length; j++) {
      const costo = a[i - 1] === b[j - 1] ? 0 : 1;
      actual[j] = Math.min(actual[j - 1] + 1, anterior[j] + 1, anterior[j - 1] + costo);
    }
    anterior = actual;
  }
  return anterior[b.length];
}

const parecido = (a, b) => {
  const largo = Math.max(a.length, b.length);
  return largo === 0 ? 1 : 1 - distanciaEdicion(a, b) / largo;
};

// "BCO" es "BLANCO" salteando letras: así se abrevia en las planillas. Se pide que
// arranquen igual para que "OL" no sea abreviatura de cualquier cosa.
function esAbreviatura(corto, largo) {
  if (corto.length < 2 || corto.length >= largo.length) return false;
  if (corto[0] !== largo[0]) return false;
  let i = 0;
  for (const letra of largo) if (letra === corto[i]) i += 1;
  return i === corto.length;
}

// Parecido palabra por palabra, emparejando cada token con el que mejor le calce.
// Es lo que permite reconocer la abreviatura de una sola palabra dentro del nombre.
function parecidoPorTokens(a, b) {
  const tokensA = a.split(" ").filter(Boolean);
  const tokensB = b.split(" ").filter(Boolean);
  if (!tokensA.length || !tokensB.length) return 0;

  const usados = new Set();
  let suma = 0;
  let hayTokenSuelto = false;

  tokensA.forEach((tokenA) => {
    let mejor = 0;
    let mejorIndice = -1;
    tokensB.forEach((tokenB, indice) => {
      if (usados.has(indice)) return;
      let puntaje;
      if (tokenA === tokenB) puntaje = 1;
      else if (esAbreviatura(tokenA, tokenB) || esAbreviatura(tokenB, tokenA)) puntaje = 0.9;
      else puntaje = parecido(tokenA, tokenB);
      if (puntaje > mejor) {
        mejor = puntaje;
        mejorIndice = indice;
      }
    });
    if (mejorIndice >= 0) usados.add(mejorIndice);
    if (mejor < 0.5) hayTokenSuelto = true;
    suma += mejor;
  });

  const promedio = suma / Math.max(tokensA.length, tokensB.length);
  // Un token que no encuentra nada parecido suele ser justamente el que distingue al
  // equipo ("A" vs "B", "AZUL" vs "BLANCO"): si aparece, la sugerencia no vale.
  return hayTokenSuelto ? promedio * 0.6 : promedio;
}

// Cuánto se parecen dos nombres de equipo, entre 0 y 1.
//
// A propósito se compara sólo palabra por palabra, y NO con la distancia de edición
// sobre el nombre entero: "CRUZ DEL SUR A" y "CRUZ DEL SUR B" difieren en una letra
// sobre catorce (0.93 de parecido global) pero son equipos distintos. Lo que decide es
// si cada palabra encuentra su equivalente, que es donde se nota la diferencia.
export function similitudNombres(a, b) {
  const na = normalizarNombre(a);
  const nb = normalizarNombre(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  return parecidoPorTokens(na, nb);
}

// Candidatos de la zona ordenados por parecido con el nombre del Excel.
function rankear(nombreExcel, candidatos) {
  return candidatos
    .map((c) => ({ id: c.id, nombre: c.nombre, score: similitudNombres(nombreExcel, c.nombre) }))
    .sort((x, y) => y.score - x.score);
}

// Busca el equipo de la zona que corresponde a un nombre del Excel.
//
// Sólo match exacto (ya normalizado). Deliberadamente NO se hace match parcial por
// inclusión: los nombres reales se solapan de a pares —"LAS ORANGE" (zona C) está
// contenido en "LAS ORANGE BLUE" (zona D), y están MUVI AZUL/BLANCO/CELESTE, CIMA
// VERDE/NEGRO, TDH B/D, DON BOSCO AZUL/BLANCO/CELESTE— así que aceptar coincidencias
// parciales cargaría partidos con el equipo equivocado sin que nadie se entere.
// Lo que no matchea cae en la previsualización para que lo resuelva el usuario.
function buscarEquipo(nombreExcel, candidatos) {
  const buscado = normalizarNombre(nombreExcel);
  if (!buscado) return null;
  return candidatos.find((c) => c.normalizado === buscado) || null;
}

// Equipos de la zona con su nombre resuelto contra torneo.equipos[].
export function candidatosDeZona(zoneTeamIds = [], teams = []) {
  return zoneTeamIds
    .map((teamId) => {
      const equipo = teams.find((t) => t.id === teamId);
      if (!equipo) return null;
      return { id: teamId, nombre: equipo.name, normalizado: normalizarNombre(equipo.name) };
    })
    .filter(Boolean);
}

// Clasifica las filas crudas del Excel contra la zona actual.
//
// filas: [{ local, visitante, hora, cancha, fila }]  (nombres crudos del archivo)
//
// Cada partido devuelto lleva además una `clave` propia (su posición en `filas`), que es
// con lo que hay que identificarlo. `fila` es el número de fila del Excel y NO es único:
// el cuadro tiene un bloque de columnas por cancha, así que dos partidos que están uno al
// lado del otro comparten el número de fila. Sirve para mostrárselo al usuario, no para
// indexar.
//
// Devuelve:
//   importables  -> las dos puntas resueltas dentro de la zona
//   repetidos    -> el mismo cruce más de una vez con datos distintos; elige el usuario
//   ambiguas     -> una punta resuelta y la otra no (typo probable); las resuelve el
//                   usuario, con `sugerencia` (el más parecido) y `ranking` para el select
//   ajenas       -> ninguna punta en la zona: son de otra zona, se descartan en silencio
//   invalidas    -> la fila no sirve (falta un equipo, o local === visitante)
export function resolverFixture(filas = [], zoneTeamIds = [], teams = []) {
  const candidatos = candidatosDeZona(zoneTeamIds, teams);
  const resultado = { importables: [], ambiguas: [], ajenas: [], invalidas: [] };

  filas.forEach((fila, indice) => {
    const nombreLocal = String(fila.local || "").trim();
    const nombreVisitante = String(fila.visitante || "").trim();

    if (!nombreLocal || !nombreVisitante) {
      resultado.invalidas.push({ ...fila, clave: indice, motivo: "La fila no tiene los dos equipos" });
      return;
    }

    const local = buscarEquipo(nombreLocal, candidatos);
    const visitante = buscarEquipo(nombreVisitante, candidatos);

    // Ninguno de los dos es de esta zona: la fila es de otra zona del mismo archivo.
    if (!local && !visitante) {
      resultado.ajenas.push({ ...fila, clave: indice });
      return;
    }

    const comun = {
      clave: indice,
      fila: fila.fila,
      nombreLocal,
      nombreVisitante,
      hora: normalizarHora(fila.hora),
      cancha: normalizarCancha(fila.cancha),
    };

    if (local && visitante) {
      if (local.id === visitante.id) {
        resultado.invalidas.push({ ...comun, motivo: "Un equipo no puede jugar contra sí mismo" });
        return;
      }
      resultado.importables.push({ ...comun, homeTeamId: local.id, awayTeamId: visitante.id });
      return;
    }

    // Una sola punta reconocida: puede ser un typo o un partido interzonal. Se ofrece
    // el equipo de la zona que más se parezca al nombre que no se pudo reconocer.
    const sinReconocer = local ? nombreVisitante : nombreLocal;
    const reconocido = local ? local.id : visitante.id;
    const ranking = rankear(sinReconocer, candidatos).filter((c) => c.id !== reconocido);
    const mejor = ranking[0];

    resultado.ambiguas.push({
      ...comun,
      homeTeamId: local ? local.id : "",
      awayTeamId: visitante ? visitante.id : "",
      ranking,
      sugerencia: mejor && mejor.score >= UMBRAL_SUGERENCIA ? mejor : null,
    });
  });

  return separarRepetidos(resultado);
}

// Un mismo cruce puede figurar más de una vez en el cuadro, con datos distintos: pasa
// cuando un equipo se da de baja y queda el partido programado arriba (con hora y
// cancha) y el walkover más abajo (sin hora). Cuál vale no se puede decidir solo, así
// que se apartan para que el usuario elija en la previsualización.
function separarRepetidos(resultado) {
  const porCruce = new Map();
  resultado.importables.forEach((partido) => {
    const clave = [partido.homeTeamId, partido.awayTeamId].sort().join("|");
    if (!porCruce.has(clave)) porCruce.set(clave, []);
    porCruce.get(clave).push(partido);
  });

  const importables = [];
  const repetidos = [];
  porCruce.forEach((variantes, clave) => {
    if (variantes.length === 1) importables.push(variantes[0]);
    // Sin hora es el walkover: va primero porque es la corrección más reciente.
    else repetidos.push({ clave, variantes: [...variantes].sort((a, b) => (a.hora ? 1 : 0) - (b.hora ? 1 : 0)) });
  });

  return { ...resultado, importables, repetidos };
}
